import { useState } from 'react';
import {
    Box,
    FlexBox,
    Icon,
    Link,
    ModalDialog,
    ModalDialogBody,
    ModalDialogCloseButton,
    ModalDialogContent,
    ModalDialogHeader,
    ModalDialogNav,
    ModalDialogTitle,
    ShortTaxMessage,
    Typography,
    responsive,
    useScreenClass,
} from '@vp/swan';
import { Currency, price, useVatMessaging } from '@99designs/design-services-common';
import { __ } from '@99designs/i18n';
import { NormalisedBriefProduct } from '../BriefContext';
import { HowItWorks } from '../HowItWorks/HowItWorks';
import { StepProductCard } from './StepProductCard';
import { BriefProductFragment } from './brief.generated';

const ResponsiveFlexBox = responsive(FlexBox);
const ResponsiveTypography = responsive(Typography);
const ResponsiveShortTaxMessage = responsive(ShortTaxMessage);

export function Information({
    product,
}: {
    product?: BriefProductFragment | NormalisedBriefProduct | null;
}) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const vatMessage = useVatMessaging();
    const screenClass = useScreenClass();

    if (!product) {
        return null;
    }

    const isMobile = screenClass === 'xs';
    const pricing = product.pricing.amount;
    const extraInfoText = __("What's included", {
        _context:
            "Label for a button that opens a modal with more information about what's included in the product",
    });

    const ExtraInfo = () => (
        <Link
            skin="standard"
            marginRight={6}
            paddingTop={2}
            onClick={() => setIsModalOpen(!isModalOpen)}
            withIcon={true}
        >
            {extraInfoText}
            <Icon iconType="info" size="20p" />
        </Link>
    );

    const PricingInformation = () => (
        <Box>
            <ResponsiveTypography
                fontSkin="title-section"
                textAlign="right"
                sm={{ textAlign: 'left' }}
            >
                {price({
                    amount: pricing.amountInCents,
                    currency: pricing.currency as Currency,
                    decimalPlaces: 2,
                })}
            </ResponsiveTypography>
            {vatMessage && (
                <ResponsiveShortTaxMessage
                    fontSkin="body-standard"
                    textColor="subtle"
                    textAlign="left"
                    sm={{ textAlign: 'right' }}
                >
                    {vatMessage}
                </ResponsiveShortTaxMessage>
            )}
        </Box>
    );

    const costAndInformation = isMobile ? (
        <>
            <PricingInformation />
            <ExtraInfo />
        </>
    ) : (
        <>
            <ExtraInfo />
            <PricingInformation />
        </>
    );

    return (
        <>
            <ResponsiveFlexBox
                flexDirection="column"
                alignItems="flex-start"
                sm={{
                    flexDirection: 'row',
                    alignItems: 'center',
                    mr: 5,
                }}
            >
                {costAndInformation}
            </ResponsiveFlexBox>
            <InformationModal product={product} isOpen={isModalOpen} setIsOpen={setIsModalOpen} />
        </>
    );
}

interface InformationModalProps {
    product: BriefProductFragment | NormalisedBriefProduct;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
}

function InformationModal(props: InformationModalProps) {
    return (
        <ModalDialog isOpen={props.isOpen} onRequestDismiss={() => props.setIsOpen(false)}>
            <ModalDialogContent aria-labelledby="modal dialog general" style={{ width: '600px' }}>
                <ModalDialogNav>
                    <ModalDialogCloseButton accessibleText={__('Close')} />
                </ModalDialogNav>
                <ModalDialogHeader>
                    <ModalDialogTitle>{__("What's included")}</ModalDialogTitle>
                </ModalDialogHeader>
                <ModalDialogBody>
                    <StepProductCard product={props.product} />
                    <Box mt={7}>
                        <HowItWorks isModal />
                    </Box>
                </ModalDialogBody>
            </ModalDialogContent>
        </ModalDialog>
    );
}
